import { Typography } from '@mui/material';
import { ErrorInfoIcon } from 'assets/icons';
import clsx from 'clsx';
import { ReactElement, ReactNode } from 'react';

export interface ErrorAlertProps {
  /**
   * Additional class name to apply to this component. Only effective while loading or in error state.
   */
  className?: string;

  /**
   * Accessibility role for this component. Defaults to "alert".
   */
  role?: string;

  /**
   * Set if the call errored. Takes precedence over `children`.
   */
  error?: string | Error;

  /**
   * If set, will be prefixed before a possible error message.
   */
  errorPrefix?: string;

  /**
   * Gets displayed if `error` is falsy.
   */
  children?: ReactNode;
}

export function ErrorAlert({
  className,
  role = 'alert',
  error,
  errorPrefix,
  children,
}: Readonly<ErrorAlertProps>): ReactElement<ErrorAlertProps> {
  return (
    <div
      data-testid="error"
      className={clsx('flex items-center justify-center whitespace-pre-line p-1', className)}
      role={role}
    >
      {children || (
        <>
          <ErrorInfoIcon className="text-error-500 w-16" />
          <Typography color="error">
            {errorPrefix}
            {error && typeof error === 'object' ? error.message : error}
          </Typography>
        </>
      )}
    </div>
  );
}
